import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useTheme } from './useTheme';
import { useScreen } from './useScreen';

const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', logoGrad:'linear-gradient(135deg, #534AB7, #1D9E75)', triGrad:'linear-gradient(135deg, #5dc1b9, #FDBE02, #eb636b)', blue:'#3B82F6' };
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const DAYS = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function Bar() { return <div style={{ width:4, height:24, borderRadius:2, background:C.triGrad, flexShrink:0 }}/>; }

export default function RentalCalendar({ userId, profile }) {
  const t = useTheme();
  const { isMobile } = useScreen();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [events, setEvents] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(null); // 'reserve' | 'block' | null
  const [form, setForm] = useState({ client_name:'', title:'', event_type:'reservation', start_date:'', end_date:'', price:'', payment_status:'paid', deposit_amount:'', notes:'' });
  const [refresh, setRefresh] = useState(0);

  const r = () => setRefresh(n => n + 1);

  useEffect(() => {
    if (!userId) return;
    const from = `${year}-${String(month+1).padStart(2,'0')}-01`;
    const toM = month === 11 ? 0 : month + 1;
    const toY = month === 11 ? year + 1 : year;
    const to = `${toY}-${String(toM+1).padStart(2,'0')}-01`;
    supabase.from('calendar_events').select('*').eq('user_id', userId).gte('start_date', from).lt('start_date', to).order('start_date').then(({ data }) => { if (data) setEvents(data); });
    supabase.from('daily_sales').select('*').eq('user_id', userId).not('rental_date', 'is', null).gte('rental_date', from).lt('rental_date', to).then(({ data }) => { if (data) setRentals(data); });
  }, [userId, year, month, refresh]);

  // Build day map
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7; // Mon=0
  const totalDays = lastDay.getDate();

  const dayMap = {};
  for (let d = 1; d <= totalDays; d++) {
    const key = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    dayMap[key] = { events: [], rentals: [], blocked: false };
  }
  events.forEach(ev => {
    const sd = ev.start_date;
    const ed = ev.end_date || sd;
    let cur = new Date(sd);
    const end = new Date(ed);
    while (cur <= end) {
      const k = cur.toISOString().split('T')[0];
      if (dayMap[k]) {
        if (ev.is_blocked) dayMap[k].blocked = true;
        else dayMap[k].events.push(ev);
      }
      cur.setDate(cur.getDate() + 1);
    }
  });
  rentals.forEach(r => {
    if (r.rental_date && dayMap[r.rental_date]) dayMap[r.rental_date].rentals.push(r);
  });

  const getDayColor = (info) => {
    if (!info) return null;
    if (info.blocked) return C.coral;
    const allItems = [...info.events, ...info.rentals];
    if (allItems.length === 0) return null;
    const hasLayaway = allItems.some(i => i.payment_status === 'layaway');
    const hasVisit = info.events.some(e => e.event_type === 'visita_td');
    if (hasVisit) return C.blue;
    if (hasLayaway) return C.mango;
    return C.turquesa;
  };

  const navMonth = (dir) => {
    let m = month + dir, y = year;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMonth(m); setYear(y); setSelectedDay(null);
  };

  const openReserve = (date) => {
    setForm({ client_name:'', title:'', event_type:'reservation', start_date:date, end_date:date, price:'', payment_status:'paid', deposit_amount:'', notes:'' });
    setShowModal('reserve');
  };

  const blockDate = async (date) => {
    await supabase.from('calendar_events').insert({ user_id:userId, start_date:date, end_date:date, is_blocked:true, event_type:'blocked', title:'Bloqueado' });
    r();
  };

  const saveReservation = async () => {
    if (!form.client_name.trim() || !form.start_date) return;
    await supabase.from('calendar_events').insert({
      user_id: userId, client_name: form.client_name.trim(), title: form.title || form.client_name.trim(),
      event_type: form.event_type, start_date: form.start_date, end_date: form.end_date || form.start_date,
      price: form.price ? Number(form.price) : null, payment_status: form.payment_status,
      deposit_amount: form.payment_status === 'layaway' ? Number(form.deposit_amount) || 0 : 0,
      notes: form.notes || null,
    });
    setShowModal(null); r();
  };

  const completePayment = async (evId) => {
    await supabase.from('calendar_events').update({ payment_status:'paid' }).eq('id', evId);
    r();
  };

  const cancelEvent = async (evId) => {
    await supabase.from('calendar_events').delete().eq('id', evId);
    r(); setSelectedDay(null);
  };

  const selectedDate = selectedDay ? `${year}-${String(month+1).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}` : null;
  const dayInfo = selectedDate ? dayMap[selectedDate] : null;
  const allDayItems = dayInfo ? [...dayInfo.events, ...dayInfo.rentals.map(r => ({ ...r, event_type:'rental_sale', client_name:r.customer_name, title:r.product_name, price:r.price, payment_status:r.payment_status, deposit_amount:r.deposit_amount }))] : [];

  // Summary
  const monthEvents = events.filter(e => !e.is_blocked);
  const monthTotal = monthEvents.reduce((s, e) => s + (e.price || 0), 0);
  const monthLayaways = monthEvents.filter(e => e.payment_status === 'layaway').length;
  const bookedDays = new Set();
  events.forEach(e => { if (!e.is_blocked) { let c = new Date(e.start_date); const end = new Date(e.end_date || e.start_date); while (c <= end) { bookedDays.add(c.getDate()); c.setDate(c.getDate() + 1); } } });
  const blockedDays = new Set();
  events.forEach(e => { if (e.is_blocked) { let c = new Date(e.start_date); const end = new Date(e.end_date || e.start_date); while (c <= end) { blockedDays.add(c.getDate()); c.setDate(c.getDate() + 1); } } });
  const availableDays = totalDays - bookedDays.size - blockedDays.size;
  const nextEvent = monthEvents.find(e => new Date(e.start_date) >= now);

  const inp = { width:'100%', padding:'10px 14px', border:`0.5px solid ${t.border}`, borderRadius:12, fontSize:14, fontFamily:font, background:t.card, color:t.text, outline:'none', boxSizing:'border-box' };

  return (
    <section style={{ marginBottom:'2.5rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}><Bar/><h2 style={{ fontFamily:serif, fontSize:isMobile?18:22, fontWeight:400, margin:0 }}>Calendario de reservaciones</h2></div>

      {/* Action buttons */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <button onClick={() => openReserve(new Date().toISOString().split('T')[0])} style={{ background:C.turquesa, color:'#fff', border:'none', borderRadius:50, padding:'8px 18px', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:font }}>📅 Apartar fecha</button>
        <button onClick={() => blockDate(new Date().toISOString().split('T')[0])} style={{ background:C.coral, color:'#fff', border:'none', borderRadius:50, padding:'8px 18px', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:font }}>🚫 Bloquear hoy</button>
      </div>

      {/* Month nav */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <button onClick={() => navMonth(-1)} style={{ background:'none', border:`0.5px solid ${t.border}`, borderRadius:50, width:36, height:36, cursor:'pointer', fontSize:16, color:t.text, display:'flex', alignItems:'center', justifyContent:'center' }}>←</button>
        <p style={{ fontFamily:serif, fontSize:18, fontWeight:400, margin:0 }}>{MONTHS[month]} {year}</p>
        <button onClick={() => navMonth(1)} style={{ background:'none', border:`0.5px solid ${t.border}`, borderRadius:50, width:36, height:36, cursor:'pointer', fontSize:16, color:t.text, display:'flex', alignItems:'center', justifyContent:'center' }}>→</button>
      </div>

      {/* Calendar grid */}
      <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:isMobile?'0.75rem':'1.25rem', marginBottom:16 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
          {DAYS.map(d => <div key={d} style={{ textAlign:'center', fontSize:11, color:t.textSecondary, fontWeight:600, padding:'4px 0' }}>{d}</div>)}
          {Array.from({ length: startWeekday }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: totalDays }).map((_, i) => {
            const d = i + 1;
            const key = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
            const info = dayMap[key];
            const color = getDayColor(info);
            const isToday = d === now.getDate() && month === now.getMonth() && year === now.getFullYear();
            const isSelected = d === selectedDay;
            return (
              <div key={d} onClick={() => setSelectedDay(selectedDay === d ? null : d)} style={{
                textAlign:'center', padding:isMobile?'6px 2px':'8px 4px', cursor:'pointer',
                borderRadius:10, fontSize:isMobile?12:14, fontWeight:isToday?700:400,
                background: isSelected ? (color || C.turquesa) + '25' : 'transparent',
                border: isToday ? `2px solid ${C.turquesa}` : isSelected ? `2px solid ${color || C.turquesa}` : '2px solid transparent',
                position:'relative',
              }}>
                {d}
                {color && <div style={{ width:6, height:6, borderRadius:'50%', background:color, margin:'2px auto 0' }} />}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display:'flex', gap:12, marginTop:10, flexWrap:'wrap', justifyContent:'center' }}>
          {[{ c:C.turquesa, l:'Pagado' }, { c:C.mango, l:'Apartado' }, { c:C.blue, l:'Visita T&D' }, { c:C.coral, l:'Bloqueado' }].map((x, i) => (
            <span key={i} style={{ fontSize:10, display:'flex', alignItems:'center', gap:4, color:t.textSecondary }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:x.c }} />{x.l}
            </span>
          ))}
        </div>
      </div>

      {/* Day detail */}
      {selectedDay && dayInfo && (
        <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:isMobile?'1rem':'1.25rem', marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <p style={{ fontFamily:serif, fontSize:16, fontWeight:400, margin:0 }}>{selectedDay} de {MONTHS[month]}</p>
            <div style={{ display:'flex', gap:6 }}>
              <button onClick={() => openReserve(selectedDate)} style={{ background:C.turquesa+'15', color:C.turquesa, border:'none', borderRadius:50, padding:'5px 12px', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:font }}>+ Reservar</button>
              {!dayInfo.blocked && <button onClick={() => blockDate(selectedDate)} style={{ background:C.coral+'15', color:C.coral, border:'none', borderRadius:50, padding:'5px 12px', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:font }}>Bloquear</button>}
            </div>
          </div>
          {dayInfo.blocked && <p style={{ fontSize:13, color:C.coral, fontWeight:600, margin:'0 0 8px' }}>🚫 Fecha bloqueada</p>}
          {allDayItems.length === 0 && !dayInfo.blocked && <p style={{ fontSize:13, color:t.textSecondary }}>Sin reservaciones este día</p>}
          {allDayItems.map((item, i) => {
            const isLayaway = item.payment_status === 'layaway';
            const remaining = isLayaway ? (item.price || 0) - (item.deposit_amount || 0) : 0;
            return (
              <div key={i} style={{ padding:'10px 0', borderBottom:i < allDayItems.length - 1 ? `0.5px solid ${t.border}` : 'none' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <span style={{ fontSize:14 }}>{item.event_type === 'visita_td' ? '🔵' : isLayaway ? '🟡' : '🟢'}</span>
                  <p style={{ fontSize:14, fontWeight:600, margin:0, flex:1 }}>{item.client_name || item.title}</p>
                  {item.price && <span style={{ fontSize:14, fontWeight:600, color:isLayaway?C.mango:C.turquesa }}>${Number(item.price).toLocaleString()}</span>}
                </div>
                {item.title && item.client_name && <p style={{ fontSize:12, color:t.textSecondary, margin:'2px 0 0' }}>{item.title}</p>}
                {isLayaway && <p style={{ fontSize:11, color:C.mango, margin:'4px 0 0' }}>Anticipo: ${(item.deposit_amount||0).toLocaleString()} — Resta: ${remaining.toLocaleString()}</p>}
                {item.notes && <p style={{ fontSize:11, color:t.textSecondary, margin:'2px 0 0', fontStyle:'italic' }}>{item.notes}</p>}
                <div style={{ display:'flex', gap:6, marginTop:6 }}>
                  {isLayaway && item.id && <button onClick={() => completePayment(item.id)} style={{ background:C.turquesa, color:'#fff', border:'none', borderRadius:50, padding:'4px 12px', fontSize:11, fontWeight:500, cursor:'pointer', fontFamily:font }}>✓ Completar pago</button>}
                  {item.id && item.event_type !== 'rental_sale' && <button onClick={() => cancelEvent(item.id)} style={{ background:'none', border:`0.5px solid ${C.coral}`, color:C.coral, borderRadius:50, padding:'4px 12px', fontSize:11, fontWeight:500, cursor:'pointer', fontFamily:font }}>Cancelar</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)', gap:10, marginBottom:16 }}>
        <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:14, padding:12, textAlign:'center' }}>
          <p style={{ fontSize:20, fontWeight:600, margin:'0 0 2px' }}>{monthEvents.length}</p>
          <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Reservaciones</p>
        </div>
        <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:14, padding:12, textAlign:'center' }}>
          <p style={{ fontSize:20, fontWeight:600, margin:'0 0 2px', color:C.turquesa }}>${monthTotal.toLocaleString()}</p>
          <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Total del mes</p>
        </div>
        <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:14, padding:12, textAlign:'center' }}>
          <p style={{ fontSize:20, fontWeight:600, margin:'0 0 2px', color:availableDays>10?C.turquesa:C.mango }}>{availableDays}</p>
          <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Días disponibles</p>
        </div>
      </div>
      {monthLayaways > 0 && <p style={{ fontSize:12, color:C.mango, margin:'0 0 8px' }}>⚠ {monthLayaways} apartado{monthLayaways>1?'s':''} pendiente{monthLayaways>1?'s':''} de pago</p>}
      {nextEvent && <p style={{ fontSize:12, color:t.textSecondary, margin:0 }}>Próxima reservación: <strong>{nextEvent.start_date}</strong> — {nextEvent.client_name}</p>}

      {/* Reserve modal */}
      {showModal === 'reserve' && (
        <>
          <div onClick={() => setShowModal(null)} style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:9998 }} />
          <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'calc(100vw - 2rem)', maxWidth:460, maxHeight:'90vh', overflowY:'auto', zIndex:9999, background:t.card, borderRadius:20, padding:isMobile?'1.25rem':'1.5rem', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
            <p style={{ fontFamily:serif, fontSize:18, fontWeight:400, margin:'0 0 16px' }}>📅 Nueva reservación</p>
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>
              <div><label style={{ fontSize:12, color:t.textSecondary, display:'block', marginBottom:3 }}>Nombre del cliente *</label><input value={form.client_name} onChange={e => setForm(f => ({...f, client_name:e.target.value}))} placeholder="Pedro García" style={inp} /></div>
              <div><label style={{ fontSize:12, color:t.textSecondary, display:'block', marginBottom:3 }}>Tipo de evento</label>
                <select value={form.event_type} onChange={e => setForm(f => ({...f, event_type:e.target.value}))} style={inp}>
                  <option value="reservation">Renta / Reservación</option>
                  <option value="fiesta">Fiesta</option>
                  <option value="evento">Evento</option>
                  <option value="visita_td">Visita T&D</option>
                </select>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <div><label style={{ fontSize:12, color:t.textSecondary, display:'block', marginBottom:3 }}>Fecha inicio *</label><input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date:e.target.value}))} style={inp} /></div>
                <div><label style={{ fontSize:12, color:t.textSecondary, display:'block', marginBottom:3 }}>Fecha fin</label><input type="date" value={form.end_date} onChange={e => setForm(f => ({...f, end_date:e.target.value}))} style={inp} /></div>
              </div>
              <div><label style={{ fontSize:12, color:t.textSecondary, display:'block', marginBottom:3 }}>Precio total $</label><input type="number" value={form.price} onChange={e => setForm(f => ({...f, price:e.target.value}))} placeholder="0" style={inp} /></div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setForm(f => ({...f, payment_status:'paid'}))} style={{ flex:1, padding:'8px', borderRadius:50, border:`1.5px solid ${form.payment_status==='paid'?C.turquesa:t.border}`, background:form.payment_status==='paid'?C.turquesa+'15':t.card, color:form.payment_status==='paid'?C.turquesa:t.text, fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:font }}>💰 Pagado</button>
                <button onClick={() => setForm(f => ({...f, payment_status:'layaway'}))} style={{ flex:1, padding:'8px', borderRadius:50, border:`1.5px solid ${form.payment_status==='layaway'?C.mango:t.border}`, background:form.payment_status==='layaway'?C.mango+'15':t.card, color:form.payment_status==='layaway'?C.mango:t.text, fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:font }}>🔒 Apartado</button>
              </div>
              {form.payment_status === 'layaway' && (
                <div><label style={{ fontSize:12, color:t.textSecondary, display:'block', marginBottom:3 }}>Anticipo $</label><input type="number" value={form.deposit_amount} onChange={e => setForm(f => ({...f, deposit_amount:e.target.value}))} placeholder="0" style={inp} />
                  {form.price && form.deposit_amount && <p style={{ fontSize:12, color:C.mango, margin:'4px 0 0' }}>Resta: ${(Number(form.price)-Number(form.deposit_amount)).toLocaleString()}</p>}
                </div>
              )}
              <div><label style={{ fontSize:12, color:t.textSecondary, display:'block', marginBottom:3 }}>Notas</label><textarea value={form.notes} onChange={e => setForm(f => ({...f, notes:e.target.value}))} placeholder="Detalles adicionales..." style={{ ...inp, minHeight:60, resize:'vertical' }} /></div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={saveReservation} disabled={!form.client_name.trim()||!form.start_date} style={{ flex:1, background:C.logoGrad, color:'#fff', border:'none', borderRadius:50, padding:'12px', fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:font }}>Guardar reservación</button>
              <button onClick={() => setShowModal(null)} style={{ background:'none', border:`0.5px solid ${t.border}`, borderRadius:50, padding:'12px 20px', fontSize:14, cursor:'pointer', fontFamily:font, color:t.textSecondary }}>Cancelar</button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
